import React, { useRef, useState } from 'react';
import { Button, Form, Input, message, Spin } from 'antd';
import {
  DeleteOutlined,
  DesktopOutlined,
  PlusOutlined,
  EditOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import useGetInfoDevice from './services/hooks/useGetInfoDevice';
import { useParams } from 'react-router-dom';
import accountApis from 'services/accountApis';
import Text from 'components/Text';
import CreateOrUpdateDeviceInfo, {
  useCreateOrUpdateDeviceInfoRef,
} from './CreateOrUpdateDeviceInfo';
import ModalConfirm, {
  ModalConfirmRef,
} from 'pages/employees/components/ModalConfirm';

interface DevicePayload {
  id: string;
  description: string;
  bitBlocker: string;
  ultraViewerId: string;
  ultraViewerPass: string;
  stationNumber: string;
}

const DeviceInformation = () => {
  const { id } = useParams();
  const [isLoading, setLoading] = useState(false);
  const createOrUpdateDeviceInfoRef = useCreateOrUpdateDeviceInfoRef();
  const modalConfirmRef = useRef<ModalConfirmRef>(null);

  const { data, loading, refetch } = useGetInfoDevice(id || '');

  const handleSaveDevice = async (device: DevicePayload): Promise<boolean> => {
    setLoading(true);

    try {
      const res = await accountApis.updateInfoDevice(id || '', device);

      if (res.status === 200) {
        await refetch();
        return true;
      }

      console.error('Failed to update device information');
      return false;
    } catch (error) {
      console.error('Error updating device information:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDevice = async (id: string) => {
    setLoading(true);

    try {
      const res = await accountApis.deleteDevice(id);

      if (res.status === 200) {
        await refetch();
        message.success('Device deleted successfully');
        modalConfirmRef.current?.close();
      } else {
        message.error('Failed to delete device');
      }
    } catch (error) {
      console.error('Error deleting device:', error);
      message.error('Error deleting device');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Header>
        <HeaderContent>
          <Title>Device Information</Title>

          <Subtitle>
            Manage computer devices, BitLocker keys and remote access
            information for your salon.
          </Subtitle>
        </HeaderContent>

        <AddButton
          type='primary'
          icon={<PlusOutlined />}
          disabled={loading || isLoading}
          onClick={() => createOrUpdateDeviceInfoRef.current?.open()}
        >
          Add Device
        </AddButton>
      </Header>

      {isLoading ? (
        <LoadingWrapper>
          <Spin />
        </LoadingWrapper>
      ) : data?.length === 0 ? (
        <EmptyDevice>
          <Text variant='BODY_1'>No devices added yet</Text>

          <Text variant='CAPTION_1' color='text_2' mb={2}>
            Add a device to manage its station number, BitLocker recovery key,
            and remote access information.
          </Text>

          <AddButton
            type='primary'
            icon={<PlusOutlined />}
            onClick={() => createOrUpdateDeviceInfoRef.current?.open()}
            disabled={loading || isLoading}
          >
            Add Device
          </AddButton>
        </EmptyDevice>
      ) : (
        <DeviceList>
          {data?.map((device: DevicePayload) => (
            <DeviceCard key={device.id}>
              <DeviceCardHeader>
                <DeviceInfo>
                  <DeviceIcon>
                    <DesktopOutlined />
                  </DeviceIcon>

                  <DeviceTitleWrapper>
                    <StationTitle>Station #{device.stationNumber}</StationTitle>

                    <StationDescription>
                      {device.description || 'Station'}
                    </StationDescription>
                  </DeviceTitleWrapper>
                </DeviceInfo>

                <Row>
                  <EditButton
                    icon={<EditOutlined />}
                    onClick={() => {
                      createOrUpdateDeviceInfoRef.current?.open(device);
                    }}
                    disabled={loading || isLoading}
                  >
                    Edit
                  </EditButton>

                  <DeleteButton
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() =>
                      modalConfirmRef.current?.open(
                        'Do you want to delete this device?',
                        'You can’t restore this device',
                        () => handleDeleteDevice(device.id),
                      )
                    }
                    disabled={loading || isLoading}
                  >
                    Delete
                  </DeleteButton>
                </Row>
              </DeviceCardHeader>

              <DeviceCardBody>
                <Row>
                  <FormItem label='Station Number'>
                    <Input
                      value={device.stationNumber}
                      readOnly
                    />
                  </FormItem>

                  <FormItem label='Description'>
                    <Input
                      value={device.description}
                      readOnly
                    />
                  </FormItem>
                </Row>

                <FormItem
                  label={
                    <BitLockerLabel>
                      BitLocker Recovery Key
                      <span> (48 digits total)</span>
                    </BitLockerLabel>
                  }
                >
                  <Input
                    value={device.bitBlocker}
                    readOnly
                  />
                </FormItem>

                <Row>
                  <FormItem label='UltraViewer ID'>
                    <Input
                      value={device.ultraViewerId}
                      readOnly
                    />
                  </FormItem>

                  <FormItem label='UltraViewer Password'>
                    <Input
                      value={device.ultraViewerPass}
                      readOnly
                    />
                  </FormItem>
                </Row>
              </DeviceCardBody>
            </DeviceCard>
          ))}
        </DeviceList>
      )}

      <CreateOrUpdateDeviceInfo
        ref={createOrUpdateDeviceInfoRef}
        onSubmit={handleSaveDevice}
      />
      <ModalConfirm ref={modalConfirmRef} />
    </Container>
  );
};

export default DeviceInformation;

const Container = styled.div`
  width: 100%;

  .ant-form {
    font-family: Poppins;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Title = styled.h2`
  margin: 0;
  color: #1d2129;
  font-size: 28px;
  font-weight: 600;
`;

const Subtitle = styled.p`
  margin: 0;
  color: #667085;
  font-size: 16px;
  line-height: 20px;
`;

const AddButton = styled(Button)`
  height: 40px;
  padding: 0 14px;
  border-radius: 5px;

  font-size: 14px;
  font-weight: 500;
  font-family: Poppins;
  display: flex;
  align-items: center;
`;

const DeviceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;
const DeviceCard = styled.div`
  width: 100%;
  background: #fff;
  border: 1px solid #edf0f5;
  border-radius: 7px;

  box-shadow: 0 2px 8px rgba(16, 24, 40, 0.04);
`;

const DeviceCardHeader = styled.div`
  min-height: 52px;
  padding: 10px 16px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  background: linear-gradient(90deg, #f4f7fc 0%, #f8faff 100%);

  border-bottom: 1px solid #edf0f5;
`;

const DeviceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DeviceIcon = styled.div`
  width: 40px;
  height: 40px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 50%;
  background: #e8f1ff;

  color: #1264e8;
  font-size: 18px;
`;

const DeviceTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const StationTitle = styled.div`
  color: #1d2129;
  font-size: 16px;
  line-height: 18px;
  font-weight: 600;
`;

const StationDescription = styled.div`
  color: #667085;
  font-size: 14px;
  line-height: 16px;
`;

const DeleteButton = styled(Button)`
  height: 36px;
  padding: 0 12px;
  border-radius: 5px;
  font-size: 15px;
  font-weight: 500;
  font-family: Poppins;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const DeviceCardBody = styled.div`
  padding: 10px 14px 8px;

  .ant-row {
    flex-direction: column;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormItem = styled(Form.Item)`
  margin-bottom: 8px;

  .ant-form-item-label {
    padding-bottom: 3px;
    text-align: left;

    > label {
      height: auto;
      font-family: Poppins;
      color: #344054;
      font-size: 14px;
      line-height: 18px;
      font-weight: 500;
    }
  }

  .ant-input,
  .ant-input-affix-wrapper {
    height: 40px;

    border-radius: 5px;
    border-color: #d9dee8;

    font-family: Poppins;
    font-size: 16px;

    &:hover,
    &:focus {
      border-color: #1677ff;
    }
  }

  .ant-input-affix-wrapper {
    padding: 0 9px;

    height: 40px;

    .ant-input {
      height: 100%;
    }
  }
`;

const BitLockerLabel = styled.span`
  span {
    color: #ff4d4f;
  }
`;

const EmptyDevice = styled.div`
  min-height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  gap: 8px;
  text-align: center;
`;

const EditButton = styled(Button)`
  height: 36px;
  padding: 0 12px;
  border-radius: 5px;
  font-size: 15px;
  font-family: Poppins;
  display: flex;
  align-items: center;
  gap: 4px;
  border-color: #1677ff;
  color: #1677ff;
  font-weight: 500;

  &:hover {
    background-color: #e6f4ff !important;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
`;
